import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import SectionHelp from '@/components/help/SectionHelp';
import VerticalExampleBox from '@/components/help/VerticalExampleBox';
import HelpTooltip from '@/components/help/HelpTooltip';
import helpContent from '@/help/helpContent';
import {
  BookOpen, Upload, Search, MessageCircle, Share2, FileText, Image,
  ChevronRight, ChevronDown, AlertTriangle, CheckCircle, Clock, Archive, Eye, X,
  Brain, Link2, Filter, Send, Wand2, Music, Video, Youtube, Copy, History,
} from 'lucide-react';

// S48 (9/N) — the full type list + Spanish labels now come from
// GET /tenant/knowledge/asset-types (backend's ASSET_TYPE_LABELS), not a
// second hardcoded (and previously incomplete, English-only) copy here.
const VISIBILITY_OPTIONS = ['internal_only', 'ai_context_only', 'shareable', 'public', 'restricted'];
const VISIBILITY_LABELS = {
  internal_only: 'Solo interno', ai_context_only: 'Solo contexto IA',
  shareable: 'Compartible', public: 'Público', restricted: 'Restringido',
};
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700', processing: 'bg-blue-50 text-blue-700',
  ready: 'bg-emerald-50 text-emerald-700', ready_without_embeddings: 'bg-amber-50 text-amber-700',
  failed: 'bg-red-50 text-red-700', archived: 'bg-gray-100 text-gray-500',
  metadata_only: 'bg-purple-50 text-purple-700',
};
const STATUS_LABELS = {
  draft: 'Borrador', processing: 'Procesando', ready: 'Listo',
  ready_without_embeddings: 'Listo (sin embeddings)', failed: 'Error',
  archived: 'Archivado', metadata_only: 'Solo metadata',
};

// S76 — medios por los que se puede enviar un documento a un cliente.
// Los valores coinciden con VALID_CHANNELS del backend (knowledge.py).
const SHARE_CHANNELS = [
  { k: 'whatsapp', l: 'WhatsApp' },
  { k: 'email', l: 'Correo electrónico' },
  { k: 'web', l: 'Página web' },
  { k: 'sms', l: 'SMS' },
  { k: 'all', l: 'Cualquier medio' },
];
const CHANNEL_LABELS = {
  whatsapp: 'WhatsApp', email: 'Correo electrónico', web: 'Página web',
  sms: 'SMS', voice: 'Llamada', internal: 'Uso interno', all: 'Cualquier medio',
};
// Estado de preparación de cada fragmento para la búsqueda de la IA.
const CHUNK_STATE_LABELS = {
  pending: 'Pendiente', processing: 'Preparando', completed: 'Listo',
  skipped: 'Búsqueda por palabras', failed: 'Con error',
};
const CHUNK_STATE_COLORS = {
  pending: 'bg-blue-50 text-blue-700', processing: 'bg-blue-50 text-blue-700',
  completed: 'bg-emerald-50 text-emerald-700', skipped: 'bg-amber-50 text-amber-700',
  failed: 'bg-red-50 text-red-700',
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// El backend devuelve una ruta relativa (/api/tenant/knowledge/...); el cliente
// final la abre desde fuera de la app, así que se comparte completa.
const toFullLink = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  return `${base}${path}`;
};

const COPY_FAIL_MSG = 'Tu navegador no permitió copiar. Selecciona el texto y cópialo manualmente.';

const copyToClipboard = (value, okMsg) => {
  if (!value) return;
  try {
    const result = navigator.clipboard.writeText(value);
    if (result && typeof result.then === 'function') {
      result.then(() => toast.success(okMsg)).catch(() => toast.error(COPY_FAIL_MSG));
    } else {
      toast.success(okMsg);
    }
  } catch (e) {
    toast.error(COPY_FAIL_MSG);
  }
};

export default function KnowledgeLibrary() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState('library');
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [assetTypes, setAssetTypes] = useState([]); // [{key, label}], from the backend

  // Upload state
  const [uploadMode, setUploadMode] = useState('file'); // 'file' | 'link'
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadType, setUploadType] = useState('custom');
  const [uploadVis, setUploadVis] = useState('internal_only');
  const [linkUrl, setLinkUrl] = useState('');
  const [textTitle, setTextTitle] = useState('');
  const [textBody, setTextBody] = useState('');
  const [textSourceMsg, setTextSourceMsg] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState('hybrid');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Ask state
  const [askQuestion, setAskQuestion] = useState('');
  const [askResult, setAskResult] = useState(null);
  const [asking, setAsking] = useState(false);

  // Detail state
  const [detail, setDetail] = useState(null);

  // Fragmentos del documento abierto (se cargan solo al abrir el acordeón)
  const [chunksOpen, setChunksOpen] = useState(false);
  const [chunks, setChunks] = useState([]);
  const [chunksTotal, setChunksTotal] = useState(0);
  const [chunksPage, setChunksPage] = useState(1);
  const [chunksLoading, setChunksLoading] = useState(false);
  const [chunksError, setChunksError] = useState(false);

  // Compartir documento con un cliente
  const [shareOpen, setShareOpen] = useState(false);
  const [shareChannel, setShareChannel] = useState('whatsapp');
  const [shareRecipient, setShareRecipient] = useState('');
  const [sharing, setSharing] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [makingShareable, setMakingShareable] = useState(false);

  // Historial (enlaces compartidos / búsquedas)
  const [logsOpen, setLogsOpen] = useState(false);
  const [logType, setLogType] = useState('share');
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(false);

  const loadAssets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterType) params.set('asset_type', filterType);
      const { data } = await api.get(`/tenant/knowledge/assets?${params}`);
      setAssets(data.assets || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filterStatus, filterType]);

  useEffect(() => { loadAssets(); }, [loadAssets]);

  useEffect(() => {
    api.get('/tenant/knowledge/asset-types')
      .then(({ data }) => setAssetTypes(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const prefill = location.state?.faqPrefill;
    if (!prefill) return;
    setTab(location.state?.tab || 'upload');
    setUploadMode('text');
    setTextTitle(prefill.title || '');
    const body = prefill.question && prefill.answer
      ? `P: ${prefill.question}\n\nR: ${prefill.answer}`
      : (prefill.answer || '');
    setTextBody(body);
    setUploadType(prefill.asset_type || 'faq');
    setUploadVis(prefill.visibility || 'ai_context_only');
    setTextSourceMsg(prefill.source_message_id || null);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const typeLabel = (key) => assetTypes.find(t => t.key === key)?.label || key;

  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Selecciona un archivo'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('title', uploadTitle || uploadFile.name);
      fd.append('asset_type', uploadType);
      fd.append('visibility', uploadVis);
      fd.append('auto_process', 'true');
      // Sprint 37 fix: the shared `api` client defaults Content-Type to
      // application/json on every request — for a FormData body that stomps
      // on the multipart boundary the browser needs to set itself, so the
      // backend never sees a parseable file. Clearing it here lets the
      // browser generate the correct multipart/form-data header.
      const { data } = await api.post('/tenant/knowledge/assets/upload', fd, {
        headers: { 'Content-Type': undefined },
      });
      toast.success(`Asset "${data.title}" subido correctamente`);
      setUploadFile(null);
      setUploadTitle('');
      setTab('library');
      loadAssets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const handleUploadText = async () => {
    if (!textBody.trim()) { toast.error('Escribe el contenido de la FAQ'); return; }
    setUploading(true);
    try {
      const { data } = await api.post('/tenant/knowledge/assets/text', {
        title: textTitle || 'FAQ desde Inbox',
        body_text: textBody.trim(),
        asset_type: uploadType === 'custom' ? 'faq' : uploadType,
        visibility: uploadVis,
        source_message_id: textSourceMsg,
      });
      toast.success(`FAQ "${data.title}" guardada en tu biblioteca`);
      setTextTitle('');
      setTextBody('');
      setTextSourceMsg(null);
      setTab('library');
      loadAssets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const handleUploadLink = async () => {
    if (!linkUrl.trim()) { toast.error('Pega un link'); return; }
    setUploading(true);
    try {
      const { data } = await api.post('/tenant/knowledge/assets/link', {
        title: uploadTitle || linkUrl, url: linkUrl.trim(),
        asset_type: uploadType === 'custom' ? 'youtube_link' : uploadType,
        visibility: uploadVis,
      });
      toast.success(`Link "${data.title}" agregado correctamente`);
      setLinkUrl('');
      setUploadTitle('');
      setTab('library');
      loadAssets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.post('/tenant/knowledge/search', {
        query: searchQuery, mode: searchMode, limit: 10,
      });
      setSearchResults(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSearching(false); }
  };

  const handleAsk = async () => {
    if (!askQuestion.trim()) return;
    setAsking(true);
    setAskResult(null);
    try {
      const { data } = await api.post('/tenant/knowledge/ask', {
        question: askQuestion, mode: 'hybrid',
      });
      setAskResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAsking(false); }
  };

  const handleArchive = async (id) => {
    try {
      await api.patch(`/tenant/knowledge/assets/${id}/archive`);
      toast.success('Asset archivado');
      loadAssets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleProcess = async (id) => {
    try {
      const { data } = await api.post(`/tenant/knowledge/assets/${id}/process`);
      toast.success(`Procesado: ${data.chunks_created || 0} chunks creados`);
      loadAssets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetDetailPanels = () => {
    setChunksOpen(false); setChunks([]); setChunksTotal(0); setChunksPage(1);
    setChunksError(false); setChunksLoading(false);
    setShareOpen(false); setShareResult(null); setShareRecipient(''); setSharing(false);
  };

  const closeDetail = () => { setDetail(null); resetDetailPanels(); };

  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/tenant/knowledge/assets/${id}`);
      resetDetailPanels();
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo abrir el documento'); }
  };

  // ── Fragmentos (cómo la IA lee el documento) ─────────────────────────
  const loadChunks = async (id, page = 1) => {
    setChunksLoading(true);
    setChunksError(false);
    try {
      const { data } = await api.get(`/tenant/knowledge/assets/${id}/chunks?page=${page}&page_size=20`);
      const rows = data.chunks || [];
      setChunks(prev => (page === 1 ? rows : [...prev, ...rows]));
      setChunksTotal(data.total || 0);
      setChunksPage(page);
    } catch (e) {
      setChunksError(true);
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudieron cargar los fragmentos');
    } finally { setChunksLoading(false); }
  };

  const toggleChunks = () => {
    if (!detail) return;
    if (chunksOpen) { setChunksOpen(false); return; }
    setChunksOpen(true);
    if (chunks.length === 0 && !chunksLoading) loadChunks(detail.id, 1);
  };

  // ── Historial de enlaces y búsquedas ─────────────────────────────────
  const loadLogs = async (type) => {
    setLogsLoading(true);
    setLogsError(false);
    try {
      const { data } = await api.get(`/tenant/knowledge/logs?log_type=${type}&page=1&page_size=20`);
      setLogs(data.logs || []);
      setLogsTotal(data.total || 0);
    } catch (e) {
      setLogsError(true);
      setLogs([]);
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar el historial');
    } finally { setLogsLoading(false); }
  };

  const toggleLogs = () => {
    if (logsOpen) { setLogsOpen(false); return; }
    setLogsOpen(true);
    loadLogs(logType);
  };

  const switchLogType = (type) => {
    if (type === logType) return;
    setLogType(type);
    setLogs([]);
    loadLogs(type);
  };

  const assetTitleById = (id) => assets.find(a => a.id === id)?.title
    || 'Documento no visible con los filtros actuales';

  // ── Compartir con un cliente ─────────────────────────────────────────
  const canShare = (a) => !!a
    && !['internal_only', 'ai_context_only'].includes(a.visibility)
    && !['archived', 'failed'].includes(a.status);

  const shareChannelsFor = (a) => {
    const allowed = a?.allowed_channels || [];
    if (allowed.length === 0 || allowed.includes('all')) return SHARE_CHANNELS;
    return SHARE_CHANNELS.filter(c => c.k === 'all' || allowed.includes(c.k));
  };

  // Atajo para que el usuario no tenga que volver a subir el documento solo
  // para poder enviárselo a un cliente. "Compartible" sigue estando disponible
  // para la IA (knowledge_search.py incluye shareable en el contexto interno).
  const handleMakeShareable = async () => {
    if (!detail) return;
    setMakingShareable(true);
    try {
      await api.put(`/tenant/knowledge/assets/${detail.id}`, { visibility: 'shareable' });
      toast.success('Listo: ahora puedes enviarle este documento a tus clientes');
      setDetail(prev => (prev ? { ...prev, visibility: 'shareable' } : prev));
      loadAssets();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cambiar la visibilidad');
    } finally { setMakingShareable(false); }
  };

  const openShare = () => {
    const options = shareChannelsFor(detail);
    setShareResult(null);
    setShareRecipient('');
    setShareChannel(options[0]?.k || 'all');
    setShareOpen(true);
  };

  const handleShare = async () => {
    if (!detail) return;
    setSharing(true);
    setShareResult(null);
    try {
      const { data } = await api.post(`/tenant/knowledge/assets/${detail.id}/share-preview`, {
        channel: shareChannel,
        recipient_reference: shareRecipient.trim() || null,
      });
      setShareResult(data);
      toast.success('Enlace listo. Cópialo y envíaselo a tu cliente.');
      if (logsOpen && logType === 'share') loadLogs('share');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo generar el enlace');
    } finally { setSharing(false); }
  };

  const tabs = [
    { k: 'library', l: 'Documentos', icon: FileText },
    { k: 'upload', l: 'Subir', icon: Upload },
    { k: 'search', l: 'Buscar', icon: Search },
    { k: 'ask', l: 'Preguntar', icon: Brain },
  ];

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-5" data-testid="knowledge-library">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> Biblioteca de Conocimiento
          </h2>
          <p className="text-sm text-[#64748B] mt-0.5">{total} documento{total !== 1 ? 's' : ''} en tu biblioteca</p>
        </div>
      </div>

      <SectionHelp module="knowledge" title={helpContent.knowledge.title} description={helpContent.knowledge.description} whatItDoesNot={helpContent.knowledge.whatItDoesNot} />
      <VerticalExampleBox module="knowledge" />

      {total > 0 && (
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 flex items-start gap-2" data-testid="skills-from-knowledge-cta">
          <Wand2 className="w-4 h-4 text-violet-500 mt-0.5" />
          <div className="text-xs text-violet-700">
            <p>Crea una habilidad que use esta biblioteca como fuente de conocimiento.</p>
            <button onClick={() => navigate('/tenant/ai/skills')} className="underline mt-1 font-medium" data-testid="skills-from-knowledge-link">Ir a Habilidades</button>
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-[#E2E8F0]">
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`knowledge-tab-${t.k}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.k ? 'border-[#0F172A] text-[#0F172A]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {/* LIBRARY TAB */}
      {tab === 'library' && (
        <div className="space-y-4" data-testid="knowledge-library-list">
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="text-xs border border-[#E2E8F0] rounded-lg px-3 py-1.5" data-testid="filter-status">
              <option value="">Todo estado</option>
              {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              className="text-xs border border-[#E2E8F0] rounded-lg px-3 py-1.5" data-testid="filter-type">
              <option value="">Todo tipo</option>
              {assetTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>

          {assets.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
              <BookOpen className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
              <p className="text-sm text-[#64748B] mb-3">Tu biblioteca está vacía. Sube documentos para que tu IA pueda consultarlos.</p>
              <Button size="sm" onClick={() => setTab('upload')} data-testid="empty-upload-cta">
                <Upload className="w-4 h-4 mr-1" /> Subir documento
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {assets.map(a => (
                <div key={a.id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3 hover:border-[#94A3B8] transition-colors cursor-pointer"
                  onClick={() => loadDetail(a.id)} data-testid={`asset-${a.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-[#F8F9FA] flex items-center justify-center flex-shrink-0">
                    {a.is_link ? <Youtube className="w-4 h-4 text-[#64748B]" />
                      : a.file_mime_type?.startsWith('image') ? <Image className="w-4 h-4 text-[#64748B]" />
                      : a.file_mime_type?.startsWith('audio') ? <Music className="w-4 h-4 text-[#64748B]" />
                      : a.file_mime_type?.startsWith('video') ? <Video className="w-4 h-4 text-[#64748B]" />
                      : <FileText className="w-4 h-4 text-[#64748B]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0F172A] truncate">{a.title}</p>
                    <p className="text-xs text-[#94A3B8]">
                      {typeLabel(a.asset_type)} · {a.is_link ? 'Link externo' : a.original_filename} · {a.is_link ? '' : `${(a.file_size_bytes / 1024).toFixed(1)}KB`}
                    </p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[a.status] || 'bg-gray-100'}`}>
                    {STATUS_LABELS[a.status] || a.status}
                  </span>
                  <span className="text-[10px] text-[#94A3B8]">{VISIBILITY_LABELS[a.visibility]}</span>
                  <ChevronRight className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />
                </div>
              ))}
            </div>
          )}

          {/* Detail modal */}
          {detail && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={closeDetail}>
              <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="asset-detail-modal">
                <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between">
                  <h3 className="text-sm font-bold text-[#0F172A]">{detail.title}</h3>
                  <button onClick={closeDetail}><X className="w-4 h-4 text-[#64748B]" /></button>
                </div>
                <div className="p-4 space-y-3 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-[#94A3B8]">Tipo:</span> {typeLabel(detail.asset_type)}</div>
                    <div><span className="text-[#94A3B8]">Visibilidad:</span> {VISIBILITY_LABELS[detail.visibility] || detail.visibility}</div>
                    <div><span className="text-[#94A3B8]">Estado:</span> <span className={`px-1.5 py-0.5 rounded ${STATUS_COLORS[detail.status]}`}>{STATUS_LABELS[detail.status]}</span></div>
                    <div className="flex items-center gap-1">
                      <span className="text-[#94A3B8]">Fragmentos:</span> {detail.chunk_count}
                      <HelpTooltip text="Para poder responder, tu IA corta el documento en pedazos cortos llamados fragmentos. Ábrelos abajo para ver exactamente qué texto quedó guardado." />
                    </div>
                    {detail.document && (
                      <>
                        <div><span className="text-[#94A3B8]">Páginas:</span> {detail.document.page_count || '—'}</div>
                        <div><span className="text-[#94A3B8]">Palabras:</span> {detail.document.word_count || '—'}</div>
                      </>
                    )}
                  </div>
                  {detail.document?.text_preview && (
                    <div>
                      <p className="text-[#94A3B8] mb-1">Vista previa del texto:</p>
                      <div className="bg-[#F8F9FA] rounded-lg p-2 text-[#334155] max-h-40 overflow-y-auto whitespace-pre-wrap">{detail.document.text_preview}</div>
                    </div>
                  )}

                  {/* ── Fragmentos: así lee tu IA este documento ── */}
                  <div className="border border-[#E2E8F0] rounded-lg">
                    <button type="button" onClick={toggleChunks} data-testid="asset-chunks-toggle"
                      className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-[#0F172A] hover:bg-[#F8F9FA] rounded-lg">
                      <span className="flex items-center gap-1.5">
                        <Eye className="w-3.5 h-3.5 text-[#64748B]" /> Ver fragmentos ({detail.chunk_count || 0})
                      </span>
                      <ChevronDown className={`w-3.5 h-3.5 text-[#64748B] transition-transform ${chunksOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {chunksOpen && (
                      <div className="border-t border-[#E2E8F0] p-3 space-y-2" data-testid="asset-chunks-panel">
                        <p className="text-[11px] text-[#64748B] leading-relaxed">
                          Así ve tu IA este documento. Si un fragmento está vacío o marcado con error, vuelve a procesar el documento.
                        </p>
                        {chunksLoading && chunks.length === 0 ? (
                          <div className="flex justify-center py-4">
                            <div className="w-5 h-5 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
                          </div>
                        ) : chunksError ? (
                          <div className="flex items-center gap-2 text-[11px] text-red-700 bg-red-50 rounded-lg px-3 py-2">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                            No pudimos cargar los fragmentos.
                            <button className="underline font-medium" onClick={() => loadChunks(detail.id, 1)} data-testid="asset-chunks-retry">Reintentar</button>
                          </div>
                        ) : chunks.length === 0 ? (
                          <p className="text-[11px] text-[#64748B]">
                            Sin fragmentos todavía. Procesa el documento para que tu IA pueda usarlo.
                          </p>
                        ) : (
                          <>
                            {chunks.map(c => (
                              <div key={c.id} className="bg-[#F8F9FA] rounded-lg p-2" data-testid={`asset-chunk-${c.chunk_index}`}>
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="text-[10px] font-medium text-[#0F172A]">Fragmento {(c.chunk_index ?? 0) + 1}</span>
                                  {c.page_number ? <span className="text-[10px] text-[#94A3B8]">Página {c.page_number}</span> : null}
                                  {c.section_title ? <span className="text-[10px] text-[#94A3B8] truncate max-w-[160px]">{c.section_title}</span> : null}
                                  {c.chunk_tokens ? <span className="text-[10px] text-[#94A3B8]">{c.chunk_tokens} palabras aprox.</span> : null}
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${CHUNK_STATE_COLORS[c.embedding_status] || 'bg-gray-100 text-gray-600'}`}>
                                    {CHUNK_STATE_LABELS[c.embedding_status] || c.embedding_status}
                                  </span>
                                </div>
                                <p className="text-[11px] text-[#334155] whitespace-pre-wrap">
                                  {c.chunk_text ? c.chunk_text : <span className="text-red-600">Este fragmento quedó vacío — vuelve a procesar el documento.</span>}
                                </p>
                              </div>
                            ))}
                            <div className="flex items-center justify-between pt-1">
                              <span className="text-[10px] text-[#94A3B8]">Mostrando {chunks.length} de {chunksTotal}</span>
                              {chunks.length < chunksTotal && (
                                <Button size="sm" variant="outline" className="text-xs" disabled={chunksLoading}
                                  onClick={() => loadChunks(detail.id, chunksPage + 1)} data-testid="asset-chunks-more">
                                  {chunksLoading ? 'Cargando...' : 'Cargar más'}
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Compartir con un cliente ── */}
                  {shareOpen && (
                    <div className="border border-[#E2E8F0] rounded-lg p-3 space-y-2" data-testid="asset-share-panel">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-[#0F172A] flex items-center gap-1.5">
                          <Share2 className="w-3.5 h-3.5 text-[#64748B]" /> Compartir con un cliente
                          <HelpTooltip text="Generamos un enlace temporal (24 horas) para que tu cliente abra este documento sin necesidad de tener cuenta. Pasado ese tiempo el enlace deja de funcionar." />
                        </p>
                        <button type="button" onClick={() => setShareOpen(false)} data-testid="asset-share-close">
                          <X className="w-3.5 h-3.5 text-[#94A3B8]" />
                        </button>
                      </div>
                      <div>
                        <label className="text-[11px] text-[#64748B] mb-1 block">¿Por dónde se lo vas a enviar?</label>
                        <select value={shareChannel} onChange={e => setShareChannel(e.target.value)}
                          className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid="asset-share-channel">
                          {shareChannelsFor(detail).map(c => <option key={c.k} value={c.k}>{c.l}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] text-[#64748B] mb-1 block flex items-center gap-1">
                          ¿A quién? (opcional)
                          <HelpTooltip text="Solo para tu registro: escribe el nombre, teléfono o correo del cliente para saber después a quién le enviaste este documento." />
                        </label>
                        <Input value={shareRecipient} onChange={e => setShareRecipient(e.target.value)}
                          placeholder="Ej. Ana Ruiz / 55 1234 5678" data-testid="asset-share-recipient" />
                      </div>
                      <Button size="sm" className="w-full text-xs" onClick={handleShare} disabled={sharing} data-testid="asset-share-submit">
                        {sharing
                          ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                          : <Link2 className="w-3.5 h-3.5 mr-1.5" />}
                        {sharing ? 'Generando enlace...' : 'Generar enlace'}
                      </Button>

                      {shareResult && (
                        <div className="space-y-2 pt-1" data-testid="asset-share-result">
                          <div className="flex items-center gap-1.5 text-[11px] text-emerald-700">
                            <CheckCircle className="w-3.5 h-3.5" /> Enlace listo. Vence el {fmtDateTime(shareResult.expires_at)}.
                          </div>
                          <div>
                            <label className="text-[11px] text-[#64748B] mb-1 block">Enlace para tu cliente</label>
                            <div className="flex gap-2">
                              <Input readOnly value={toFullLink(shareResult.signed_url)}
                                onFocus={e => e.target.select()} className="flex-1 text-[11px]" data-testid="asset-share-link" />
                              <Button size="sm" variant="outline" className="text-xs"
                                onClick={() => copyToClipboard(toFullLink(shareResult.signed_url), 'Enlace copiado')}
                                data-testid="asset-share-copy-link">
                                <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                              </Button>
                            </div>
                          </div>
                          {shareResult.message_suggestion && (
                            <div>
                              <label className="text-[11px] text-[#64748B] mb-1 block">Mensaje sugerido</label>
                              <div className="flex gap-2">
                                <div className="flex-1 bg-[#F8F9FA] rounded-lg p-2 text-[11px] text-[#334155]" data-testid="asset-share-message">
                                  {shareResult.message_suggestion}
                                </div>
                                <Button size="sm" variant="outline" className="text-xs"
                                  onClick={() => copyToClipboard(`${shareResult.message_suggestion} ${toFullLink(shareResult.signed_url)}`, 'Mensaje copiado')}
                                  data-testid="asset-share-copy-message">
                                  <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {!canShare(detail) && detail.status !== 'archived' && (
                    <div className="text-[11px] text-[#64748B] bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg px-3 py-2 space-y-2" data-testid="asset-share-blocked">
                      {['internal_only', 'ai_context_only'].includes(detail.visibility) ? (
                        <>
                          <p>
                            Este documento está marcado como “{VISIBILITY_LABELS[detail.visibility]}”, por eso no puede enviarse a un cliente.
                            Si lo haces compartible, tu IA lo sigue usando igual y además podrás mandarle el enlace a un cliente.
                          </p>
                          <Button size="sm" variant="outline" className="text-xs" onClick={handleMakeShareable}
                            disabled={makingShareable} data-testid="asset-make-shareable">
                            {makingShareable ? 'Guardando...' : 'Permitir compartirlo con clientes'}
                          </Button>
                        </>
                      ) : (
                        <p>Este documento tuvo un error al procesarse, así que todavía no puede compartirse. Vuelve a subirlo o procésalo de nuevo.</p>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2 pt-2 flex-wrap">
                    {detail.status === 'draft' && (
                      <Button size="sm" variant="outline" onClick={() => { handleProcess(detail.id); closeDetail(); }} className="text-xs">
                        <Clock className="w-3 h-3 mr-1" /> Procesar
                      </Button>
                    )}
                    {canShare(detail) && !shareOpen && (
                      <Button size="sm" variant="outline" onClick={openShare} className="text-xs" data-testid="asset-share-open">
                        <Share2 className="w-3 h-3 mr-1" /> Compartir
                      </Button>
                    )}
                    {detail.status !== 'archived' && (
                      <Button size="sm" variant="outline" onClick={() => { handleArchive(detail.id); closeDetail(); }} className="text-xs text-red-600 hover:text-red-700">
                        <Archive className="w-3 h-3 mr-1" /> Archivar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Historial: enlaces enviados y búsquedas ── */}
          <div className="bg-white border border-[#E2E8F0] rounded-xl" data-testid="knowledge-logs-card">
            <button type="button" onClick={toggleLogs} data-testid="knowledge-logs-toggle"
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-[#0F172A] hover:bg-[#F8F9FA] rounded-xl">
              <span className="flex items-center gap-2">
                <History className="w-4 h-4 text-[#64748B]" /> Historial de actividad
              </span>
              <ChevronDown className={`w-4 h-4 text-[#64748B] transition-transform ${logsOpen ? 'rotate-180' : ''}`} />
            </button>
            {logsOpen && (
              <div className="border-t border-[#E2E8F0] p-4 space-y-3" data-testid="knowledge-logs-panel">
                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={() => switchLogType('share')} data-testid="knowledge-logs-type-share"
                    className={`text-xs px-3 py-1.5 rounded-lg border font-medium ${logType === 'share' ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
                    Enlaces enviados a clientes
                  </button>
                  <button type="button" onClick={() => switchLogType('search')} data-testid="knowledge-logs-type-search"
                    className={`text-xs px-3 py-1.5 rounded-lg border font-medium ${logType === 'search' ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
                    Búsquedas recientes
                  </button>
                  <HelpTooltip text="Aquí queda registro de los enlaces que generaste para tus clientes y de las búsquedas que hiciste en la pestaña Buscar. Sirve para saber qué documento le enviaste a quién y qué está preguntando tu equipo." />
                </div>

                {logsLoading ? (
                  <div className="flex justify-center py-6">
                    <div className="w-5 h-5 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
                  </div>
                ) : logsError ? (
                  <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                    No pudimos cargar el historial.
                    <button className="underline font-medium" onClick={() => loadLogs(logType)} data-testid="knowledge-logs-retry">Reintentar</button>
                  </div>
                ) : logs.length === 0 ? (
                  <p className="text-xs text-[#64748B]">
                    {logType === 'share'
                      ? 'Sin registros todavía. Abre un documento y usa “Compartir” para enviarle un enlace a un cliente.'
                      : 'Sin registros todavía. Usa la pestaña Buscar para consultar tu biblioteca.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {logs.map(l => (
                      <div key={l.id} className="bg-[#F8F9FA] rounded-lg px-3 py-2 flex items-center gap-3" data-testid={`knowledge-log-${l.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[#0F172A] truncate">
                            {logType === 'share' ? assetTitleById(l.asset_id) : (l.query_preview || 'Búsqueda sin texto')}
                          </p>
                          <p className="text-[10px] text-[#94A3B8]">
                            {fmtDateTime(l.created_at)}
                            {logType === 'share'
                              ? ` · ${CHANNEL_LABELS[l.channel] || l.channel || 'Sin medio'}`
                              : `${l.latency_ms ? ` · respondió en ${l.latency_ms} ms` : ''}`}
                          </p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${l.status === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                          {l.status === 'success' ? 'Correcto' : 'Con error'}
                        </span>
                      </div>
                    ))}
                    <p className="text-[10px] text-[#94A3B8]">
                      Mostrando {logs.length} de {logsTotal} · se listan los más recientes primero
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* UPLOAD TAB */}
      {tab === 'upload' && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4 max-w-lg" data-testid="knowledge-upload">
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setUploadMode('file')} data-testid="upload-mode-file"
              className={`flex-1 min-w-[120px] px-3 py-2 rounded-lg text-sm font-medium border ${uploadMode === 'file' ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
              Subir archivo
            </button>
            <button onClick={() => setUploadMode('text')} data-testid="upload-mode-text"
              className={`flex-1 min-w-[120px] px-3 py-2 rounded-lg text-sm font-medium border ${uploadMode === 'text' ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
              Texto / FAQ
            </button>
            <button onClick={() => setUploadMode('link')} data-testid="upload-mode-link"
              className={`flex-1 min-w-[120px] px-3 py-2 rounded-lg text-sm font-medium border flex items-center justify-center gap-1.5 ${uploadMode === 'link' ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
              <Youtube className="w-4 h-4" /> Link
            </button>
          </div>
          {uploadMode === 'text' && textSourceMsg && (
            <p className="text-[10px] text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-2 py-1.5">
              Pre-llenado desde una conversación del Inbox — revisa el texto antes de guardar.
            </p>
          )}

          {uploadMode === 'file' ? (
            <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-6 text-center relative">
              {uploadFile ? (
                <div className="flex items-center gap-2 justify-center">
                  <FileText className="w-5 h-5 text-[#64748B]" />
                  <span className="text-sm text-[#0F172A]">{uploadFile.name}</span>
                  <button onClick={() => setUploadFile(null)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
                  <p className="text-sm text-[#64748B] mb-2">Arrastra un archivo o haz clic para seleccionar</p>
                  <p className="text-xs text-[#94A3B8]">PDF, DOCX, TXT, MD, HTML, CSV, XLSX, PPTX, imágenes, audio, video</p>
                </>
              )}
              <input type="file" className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                onChange={e => setUploadFile(e.target.files?.[0])}
                accept=".pdf,.docx,.txt,.md,.html,.csv,.xlsx,.pptx,.jpg,.jpeg,.png,.webp,.svg,.mp3,.wav,.m4a,.ogg,.mp4,.mov,.webm"
                data-testid="upload-file-input" />
            </div>
          ) : uploadMode === 'link' ? (
            <Input placeholder="https://www.youtube.com/watch?v=..." value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)} data-testid="upload-link-input" />
          ) : (
            <Textarea placeholder="P: ¿Cuál es el horario?\n\nR: Atendemos de lunes a sábado..." value={textBody}
              onChange={e => setTextBody(e.target.value)} rows={6} data-testid="upload-text-body" />
          )}

          <Input
            placeholder={uploadMode === 'text' ? 'Título de la FAQ' : uploadMode === 'file' ? 'Título del documento' : 'Título del video'}
            value={uploadMode === 'text' ? textTitle : uploadTitle}
            onChange={e => uploadMode === 'text' ? setTextTitle(e.target.value) : setUploadTitle(e.target.value)}
            data-testid="upload-title"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Tipo</label>
              <select value={uploadType} onChange={e => setUploadType(e.target.value)}
                className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid="upload-type">
                {assetTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Visibilidad</label>
              <select value={uploadVis} onChange={e => setUploadVis(e.target.value)}
                className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid="upload-visibility">
                {VISIBILITY_OPTIONS.map(v => <option key={v} value={v}>{VISIBILITY_LABELS[v]}</option>)}
              </select>
            </div>
          </div>
          {uploadMode === 'file' ? (
            <Button onClick={handleUpload} disabled={!uploadFile || uploading} className="w-full" data-testid="upload-submit">
              {uploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? 'Subiendo...' : 'Subir y procesar'}
            </Button>
          ) : uploadMode === 'link' ? (
            <Button onClick={handleUploadLink} disabled={!linkUrl.trim() || uploading} className="w-full" data-testid="upload-link-submit">
              {uploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
              {uploading ? 'Agregando...' : 'Agregar link'}
            </Button>
          ) : (
            <Button onClick={handleUploadText} disabled={!textBody.trim() || uploading} className="w-full" data-testid="upload-text-submit">
              {uploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> : <BookOpen className="w-4 h-4 mr-2" />}
              {uploading ? 'Guardando...' : 'Guardar FAQ en biblioteca'}
            </Button>
          )}
        </div>
      )}

      {/* SEARCH TAB */}
      {tab === 'search' && (
        <div className="space-y-4" data-testid="knowledge-search">
          <div className="flex gap-2">
            <Input placeholder="Buscar en la biblioteca..." value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()} className="flex-1" data-testid="search-input" />
            <select value={searchMode} onChange={e => setSearchMode(e.target.value)}
              className="text-xs border border-[#E2E8F0] rounded-lg px-3" data-testid="search-mode">
              <option value="keyword">Keyword</option>
              <option value="hybrid">Híbrido</option>
              <option value="semantic">Semántico</option>
            </select>
            <Button onClick={handleSearch} disabled={searching} data-testid="search-submit">
              <Search className="w-4 h-4" />
            </Button>
          </div>
          {searchResults && (
            <div className="space-y-3">
              {searchResults.warnings?.map((w, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="search-warning">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {w}
                </div>
              ))}
              <p className="text-xs text-[#94A3B8]">{searchResults.results?.length || 0} resultado(s) · modo: {searchResults.mode} · {searchResults.latency_ms}ms</p>
              {searchResults.results?.map((r, i) => (
                <div key={i} className="bg-white border border-[#E2E8F0] rounded-lg p-3" data-testid={`search-result-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-3.5 h-3.5 text-[#64748B]" />
                    <span className="text-sm font-medium text-[#0F172A]">{r.title}</span>
                    <span className="text-[10px] text-[#94A3B8]">{r.source_reference}</span>
                  </div>
                  <p className="text-xs text-[#334155] line-clamp-3">{r.snippet}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ASK TAB */}
      {tab === 'ask' && (
        <div className="space-y-4 max-w-2xl" data-testid="knowledge-ask">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-3">
            <div className="flex gap-2">
              <Textarea placeholder="Pregunta algo sobre tu biblioteca de documentos..." value={askQuestion}
                onChange={e => setAskQuestion(e.target.value)} rows={2} className="flex-1" data-testid="ask-input" />
            </div>
            <Button onClick={handleAsk} disabled={asking || !askQuestion.trim()} className="w-full" data-testid="ask-submit">
              {asking ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {asking ? 'Consultando...' : 'Preguntar a la biblioteca'}
            </Button>
          </div>
          {askResult && (
            <div className="space-y-3" data-testid="ask-result">
              {askResult.warnings?.map((w, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {w}
                </div>
              ))}
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Brain className="w-4 h-4 text-[#F72585]" />
                  <span className="text-xs font-medium text-[#0F172A]">Respuesta</span>
                  {askResult.confidence && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${askResult.confidence === 'high' ? 'bg-emerald-50 text-emerald-700' : askResult.confidence === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                      {askResult.confidence}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#334155] whitespace-pre-wrap">{askResult.answer}</p>
              </div>
              {askResult.sources?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-[#64748B] mb-2">Fuentes:</p>
                  {askResult.sources.map((s, i) => (
                    <div key={i} className="text-xs text-[#334155] bg-[#F8F9FA] rounded-lg p-2 mb-1">
                      <span className="font-medium">{s.title}</span> — {s.source_reference}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
